(ns nosco.shared.submission-form
  (:require
   #? (:clj [nosco.i18n.translate-server :as t]
       :cljs [nosco.i18n.translate :as t])
   [medley.core :as medley]
   [nosco.util.diff :as diff]))

(def default-field-visibility #{:ADMINS :AUTHORS :EVALUATORS :MEMBERS})

(def default-field-editability #{:ADMINS :AUTHORS})

(defn settings->editability [{:keys [editability]}]
  (if (empty? editability)
    default-field-editability
    (into #{} (map keyword) editability)))

(defn settings->visibility [{:keys [visibility]}]
  (if (empty? visibility)
    default-field-visibility
    (into #{} (map keyword) visibility)))

(defn idea-template-field-visible? [{:keys [viewer-admin? viewer-evaluator? viewer-author? viewer-owner? viewer-editor? idea field]}]
  (let [visibility (settings->visibility (:settings field))
        idea-state (-> idea :state keyword)
        idea-published? (or (= idea-state :PUBLISHED)
                            (= idea-state :publish))]
    (cond (and (contains? visibility :ADMINS) viewer-admin?)
          true
          (and (contains? visibility :AUTHORS)
               (or (not idea-published?)
                   viewer-author?
                   viewer-owner?
                   viewer-editor?))
          true
          (and (contains? visibility :EVALUATORS)
               idea-published?
               viewer-evaluator?)
          true
          (contains? visibility :MEMBERS)
          true
          :else
          false)))

(defn stage-template-field-visible? [{:keys [viewer-admin? field]}]
  (let [visibility (settings->visibility (:settings field))]
    (cond (and (contains? visibility :ADMINS) viewer-admin?)
          true
          (contains? visibility :AUTHORS)
          true
          (contains? visibility :EVALUATORS)
          true
          (contains? visibility :MEMBERS)
          true
          :else
          false)))

(defn idea-template-field-can-edit? [{:keys [viewer-admin? viewer-author? viewer-owner? viewer-editor? idea field]}]
  (let [editability (settings->editability (:settings field))
        idea-state (-> idea :state keyword)
        idea-published? (= idea-state :PUBLISHED)]
    (cond (and (contains? editability :ADMINS)
               viewer-admin?)
          true
          (and (contains? editability :AUTHORS)
               (or (not idea-published?)
                   viewer-author?
                   viewer-owner?
                   viewer-editor?))
          true
          :else
          false)))

(defn stage-template-field-can-edit? [{:keys [viewer-admin? field]}]
  (let [editability (settings->editability (:settings field))]
    (cond (and (contains? editability :ADMINS) viewer-admin?)
          true
          (contains? editability :AUTHORS)
          true
          (contains? editability :MEMBERS)
          true
          :else
          false)))

;;; Stage overrides

(def default-stage-overrides
  {:visible true
   :readonly false
   ;; required nil means, use the field-level required value
   ;; this will eventually get resolved to true/false
   :required nil})

(defn lookup-stage-override
  "Given the stageOverrides of a field, return the overrides for the given stage-id, falling back
   to the default stage overrides if the stage is not found"
  [field stage-id]
  (let [stage-overrides (:stageOverrides field)]
    (merge
     (assoc default-stage-overrides :stageId (str stage-id))
     (first
      (filter (fn [{:keys [stageId]}]
                (= (str stageId) (str stage-id)))
              stage-overrides)))))

;;; Utilities to process submission forms
(defn add-stage-overrides
  "Add ::stage-visibility, ::stage-readonly, and override :required to the fields in the master form,
  based on the stage overrides"
  [master-form stage-id]
  (->> master-form
       (map (fn [field]
              (let [current (lookup-stage-override field stage-id)
                    required-override (:required current)
                    required-field (:required field)
                    required-applied (if (some? required-override)
                                       required-override
                                       required-field)]
                (assoc field
                       ::stage-readonly (:readonly current)
                       ::stage-visibility (:visible current)
                       :required-master-field required-field ;; only for debugging
                       :required required-applied))))))

(defn stage-overrides-need-update?
  "Are the stage overrides changed between stages, such as the ideas
   need to be updated?"
  [master-form current-stage-id new-stage-id]
  (let [master-form-current (add-stage-overrides master-form current-stage-id)
        master-form-new (add-stage-overrides master-form new-stage-id)
        changes (map (fn [current-field new-field]
                       (let [current-overrides (:stageOverrides current-field)
                             new-overrides (:stageOverrides new-field)]
                         (or
                          ;; something wasn't required, and now is
                          (and (not (:required current-field))
                               (:required new-field))
                          ;; something wasn't visible, and now is
                          (and (not (::stage-visibility current-overrides))
                               (::stage-visibility new-overrides)))))
                     master-form-current
                     master-form-new)]
    (some true? changes)))

;; Make required-and-not-editable fields not-required
(defn- update-stage-template-items-required [template viewer]
  (let [viewer-admin? (:isAdmin viewer)]
    (->> (for [field template]
           (let [can-edit? (stage-template-field-can-edit? {:viewer-admin? viewer-admin?
                                                            :field field})]
             (if can-edit?
               field
               (assoc field :required false))))
         vec)))

(defn stage-visible-field-ids [form stage-id]
  (let [with-visibility (add-stage-overrides form stage-id)]
    (->> with-visibility
         (filter ::stage-visibility)
         (map :id)
         (into #{}))))

(defn- keywordize-type [template-item]
  (update template-item :type keyword))

(defn- conditional-options-set [template-item]
  (medley/update-existing-in template-item [:settings :conditional :optionIds] set))

(defn process-template-item
  "Convert a GraphQL templateItem to be used in a form state."
  [template-item]
  (->
   template-item
   (keywordize-type)
   (conditional-options-set)))

(defn ideabox->stage-submission-form
  "Returns a submission form ready to be used to create a new idea or update an idea to a new stage
    * either uses masterSubmissionForm if exists, or the stage ideaTemplate
    * adds a :stage-visibility boolean to the masterSubmission form
    * ensures that non editable fields are not required
    * ensures that graphql types are keywordized and made into sets as needed

     "
  [ideabox stage viewer]
  (let [master-form (some-> (:masterSubmissionForm ideabox)
                            (add-stage-overrides (:id stage)))
        default-stage-template (:ideaTemplate stage)
        form-to-use (map process-template-item
                         (or master-form default-stage-template))]

    (update-stage-template-items-required form-to-use viewer)))

(defn ideabox->initial-submission-form
  "Returns a submission form ready to be used to create a new idea
    * either uses masterSubmissionForm if exists, or the defaultStage ideaTemplate
    * adds a ::stage-visibility boolean to the masterSubmission form
    * ensures that non editable fields are not required
    * ensures that graphql types are keywordized and made into sets as needed

     "
  [ideabox viewer]
  (ideabox->stage-submission-form ideabox (:defaultStage ideabox) viewer))

(defn compute-unified-options [idea-item stage-item]
  ;; we use the diff algorithm to get as close to the correct order as possible
  (let [idea-options (get idea-item :options)
        idea-options-map (into {} (map (juxt :id identity)) idea-options)
        stage-options (get stage-item :options)
        stage-options-map (into {} (map (juxt :id identity)) stage-options)
        idea-option-ids (map :id idea-options)
        stage-options-ids (map :id stage-options)
        diff (diff/layout-ids idea-option-ids stage-options-ids)
        unified-ids (mapv :id diff)]
    (for [id unified-ids]
      {:idea-option (get idea-options-map id)
       :stage-option (get stage-options-map id)})))

(defn update-idea-template-items-required [idea-template idea viewer]
  (let [viewer-admin? (:isAdmin viewer)
        viewer-author? (= (:id viewer) (-> idea :author :id))
        viewer-owner? (some #(= (:id %) (:id viewer)) (:owners idea))
        viewer-editor? (some #(= (:id %) (:id viewer)) (:editors idea))]
    (mapv (fn [field]
            (let [can-edit? (idea-template-field-can-edit?
                             {:viewer-admin? viewer-admin?
                              :viewer-author? viewer-author?
                              :viewer-owner? viewer-owner?
                              :viewer-editor? viewer-editor?
                              :idea idea
                              :field field})]
              (cond-> field
                (not can-edit?)
                (assoc :required false)))) idea-template)))

(defn select-type? [field-type]
  (or (#{:SELECT_SINGLE :SELECT_MULTIPLE :SELECT_MULTIPLE_EXTENSIBLE :DROPDOWN} field-type)
      (#{"select" "checkbox" "checkbox_extensible" "radio"} field-type)))

(defn- compute-selected-options [options]
  (into #{}
        (comp (filter :selected) (map :id))
        options))

;; we want to always show all the options already in the idea template
;; and if there's new elements in the stage, show them too
(defn- merge-field-options [unified-options selected-options]
  (->> (for [{:keys [idea-option stage-option]} unified-options
             ;; Remove options that have been removed from the stage template and are unselected
             :when (or stage-option (contains? selected-options (:id idea-option)))]
         ;; Return the stage first since it might have
         ;; edits in the titles etc - then fallback to the
         ;; idea for removed options for completeness
         (or stage-option idea-option))
       (mapv #(select-keys % [:id :title :defaultSelected]))))

(defn idea->submission-form-legacy
  "Return the submission form for an idea, (without values!), somewhat updated based on the stage template,
  the stage submission form and editability/required fields "
  [idea]
  (let [idea-template (:template idea)
        idea-stage (:stage idea)
        stage-template (map process-template-item (:ideaTemplate idea-stage))
        stage-template-map (into {} (map (juxt :id identity) stage-template))]
    (for [idea-item idea-template
          :let [ideabox-template-item (-> idea-item
                                          :ideaboxTemplateItem
                                          process-template-item)
                stage-item (get stage-template-map (:id ideabox-template-item))

                type (:type ideabox-template-item)
                unified-options (compute-unified-options (:ideaboxTemplateItem idea-item) stage-item)
                is-select-type? (select-type? type)
                merged-options (when is-select-type?
                                 (merge-field-options unified-options
                                                      (-> idea-item :value :options compute-selected-options)))
                merged-order-by (or (-> stage-item :settings :orderBy)
                                    (-> ideabox-template-item :settings :orderBy))]]
      (cond-> ideabox-template-item
        is-select-type?
        (assoc :options merged-options)
        is-select-type?
        (assoc-in [:settings :orderBy] merged-order-by)))))

;; Detecting submission form changes
;; This is only relevant for channels that have a masterSubmissionForm, i.e. they
;; opt-in to this behaviour.
;; There are many scenarios where the submission form of an idea changes
;; - the admin edits the ideabox submission form behind the scenes
;; - the idea moves to a new stage within the same channel
;; - the idea moves to a new stage in a new channel
;; The aim is to help the author go to the new submission form, so instead of
;; showing the side-by-side diff view, we want a single column view, with the
;; changes highlighed per-field:
;;
;; a) the order of the fields is the one of the new form
;; b) new fields are highlighted in green
;; c) same fields are just plain fields
;; d) removed fields are read-only and highlighted in red
;; e) mutated fields are TBD (need to show both titles/descriptions)
;; f) minor changes such as option changes or required etc are silently updated
;;
;; In most cases, the single-column layout would work best as fields are usually added
;; so the focus would be on the few added fields per stage.
;; However, if you move an idea to a completely new channel, the forms might be wildly different
;; so in that case, we can show a side-by-side view just to ease the migration.
;; TODO we can also let the author update the existing form without migrating to the
;; new one.
;; TODO we can always offer the option to show a side-by-side comparison

(defn- idea->submission-form-new
  "Return the submission form for an idea, (without values!), updated to reflect
   changes in the master submission form, e.g. mutations of the master form,
   and stage-based visibility"
  [idea]
  (let [previous-stage-id (-> idea :previousStage :id)
        idea-items (mapv (fn [field]
                           (update field :ideaboxTemplateItem process-template-item))
                         (:template idea))
        master-form (-> (:masterSubmissionForm (:ideabox idea))
                        (add-stage-overrides (:id (:stage idea)))
                        (->> (map process-template-item)))
        idea-fields-map (into {} (map (juxt #(-> % :ideaboxTemplateItem :id) identity) idea-items))
        ideabox-template-map (into {} (map (juxt :id identity) master-form))
        layout (diff/layout-ids (map #(-> % :ideaboxTemplateItem :id) idea-items)
                                (map :id master-form))]
    (for [{:keys [id type]} layout
          :let [idea-item (get idea-fields-map id)
                idea-template-item (:ideaboxTemplateItem idea-item)
                ideabox-template-item (get ideabox-template-map id)
                 ;; TODO this is not enough - what if the previous stage doesn't exist anymore?
                 ;; or the idea is in a new channel? Should we bake somehow the stage-visibility
                 ;; into the idea template? perhaps a new field in the stageOverrides called
                 ;; lastStageVisibility that is calculated upon save?
                last-stage-visibility (:visible (lookup-stage-override ideabox-template-item
                                                                       previous-stage-id))
                 ;; if the stage-visibility changed, we don't highlight that,
                 ;; only the physical removals
                field-removed (= type :removal)
                field-added (or (= type :addition)
                                (and (not last-stage-visibility)
                                     (::stage-visibility ideabox-template-item)))
                field-to-use (if field-removed idea-template-item ideabox-template-item)
                is-select-type? (select-type? type)
                merged-options (when is-select-type?
                                 (->> (compute-unified-options idea-template-item ideabox-template-item)
                                      (map #(select-keys % [:id :title :defaultSelected]))))
                merged-order-by (or (-> idea-item :settings :orderBy)
                                    (-> idea-template-item :settings :orderBy))]]
       ;; TODO if the idea already has some content in the addition,
      ;; we don't need to mark it as added
      ;; similarly, if the field is empty and removed, we don't need to mark it as removed
      (cond-> field-to-use
        field-removed
        (assoc ::form-change-type ::removal)

        field-added
        (assoc ::form-change-type ::addition)

        is-select-type?
        (assoc :options merged-options)

        is-select-type?
        (assoc-in [:settings :orderBy] merged-order-by)

      ;; TODO detect changes, but we only care for title/description, not the whole array of checking
      ;; that template_diff is doing
        ))))

(defn idea->submission-form [idea viewer]
  (->
   (if (:masterSubmissionForm (:ideabox idea))
     (idea->submission-form-new idea)
     (idea->submission-form-legacy idea))
   (update-idea-template-items-required idea viewer)
   (vec)))

(defn visibility-message [#?(:clj locale) settings]
  (let [visibility (settings->visibility settings)]
    (when (not= default-field-visibility visibility)
      (case visibility
        #{:ADMINS :AUTHORS :EVALUATORS} (t/t #?(:clj locale) "Only admins, authors and evaluators can view")
        #{:ADMINS :EVALUATORS} (t/t #?(:clj locale) "Only admins and evaluators can view")
        #{:ADMINS :AUTHORS} (t/t #?(:clj locale) "Only admins and authors can view")
        #{:ADMINS} (t/t #?(:clj locale) "Only admins can view")))))

(defn editability-message [#?(:clj locale) settings]
  (let [editability (settings->editability settings)]
    (when (not= default-field-editability editability)
      (case editability
        #{:ADMINS :AUTHORS :EVALUATORS} (t/t #?(:clj locale) "Only admins, authors and evaluators can edit")
        #{:ADMINS :EVALUATORS} (t/t #?(:clj locale) "Only admins and evaluators can edit")
        #{:ADMINS :AUTHORS} (t/t #?(:clj locale) "Only admins and authors can edit")
        #{:ADMINS} (t/t #?(:clj locale) "Only admins can edit")))))
